/**
 * Wire types of the purchase order dispatch API (staff-api `routes/procurement-dispatch.ts` and
 * `routes/supplier-dispatch-settings.ts`, batch 8 T12a, #3033). The portal shows what the server says: nothing
 * here re-implements the dispatch state machine.
 */

export const DISPATCH_CHANNELS = ['email', 'api'] as const;
export type DispatchChannel = (typeof DISPATCH_CHANNELS)[number];

export const DISPATCH_STATUSES = [
  'queued',
  'sending',
  'sent',
  'failed',
  'uncertain',
  'cancelled',
] as const;
export type DispatchStatus = (typeof DISPATCH_STATUSES)[number];

export const SUPPLIER_DISPATCH_MODES = ['manual', 'email', 'api'] as const;
export type SupplierDispatchMode = (typeof SUPPLIER_DISPATCH_MODES)[number];

export const DOCUMENT_LANGUAGES = ['fi', 'en'] as const;
export type DocumentLanguage = (typeof DOCUMENT_LANGUAGES)[number];

/** A page of a keyset-paginated list. The cursor is opaque. */
export interface CursorPage<T> {
  data: T[];
  pagination: { hasMore: boolean; nextCursor: string | null };
}

/** State and codes only; never content (`GET .../dispatches`, needs `purchase_orders.read`). */
export interface DispatchSummary {
  id: string;
  purchaseOrderId: string;
  channel: DispatchChannel | 'manual';
  status: DispatchStatus;
  documentVersion: number;
  snapshotSha256: string;
  attemptCount: number;
  requestedBy: string | null;
  requestedAt: string | null;
  sentAt: string | null;
  confirmedAt: string | null;
  lastErrorCode: string | null;
  cancelRequestedAt: string | null;
  cancelRequestedBy: string | null;
  reconciliation: 'sent' | 'not_sent' | null;
  reconciledBy: string | null;
  reconciledAt: string | null;
  updatedAt: string | null;
  requestMayHaveLeft: boolean;
  outcomeKnown: boolean;
  reconciliationRequired: boolean;
  supplierCancellation: 'not_performed';
}

export interface SnapshotLine {
  lineId: string;
  lineNumber: number;
  partNumber: string;
  sku: string;
  description: string;
  quantity: number;
  unit: string;
  unitPriceNetCents: string;
  discountPercent: string;
  lineNetCents: string;
  notes: string | null;
}

/** The frozen purchase order document. Cost-bearing: only with `purchase_orders.dispatch`. */
export interface DispatchSnapshot {
  documentVersion: number;
  channel: DispatchChannel;
  language: DocumentLanguage;
  recipient: { email: string } | null;
  supplier: { code: string; name: string };
  order: {
    orderNumber: string;
    orderDate: string;
    expectedDeliveryDate: string | null;
    notes: string | null;
    currency: string;
  };
  lines: SnapshotLine[];
  totals: { linesNetCents: string; shippingNetCents: string };
}

export interface RenderedEmail {
  to: string;
  subject: string;
  text: string;
  /** Never rendered as markup by the portal. */
  html?: string;
}

export interface RenderedApiRequest {
  reference?: string;
  orderNumber: string;
  language?: string;
  lines: unknown[];
}

export interface DispatchPreview {
  purchaseOrderId: string;
  channel: DispatchChannel;
  documentVersion: number;
  snapshotSha256: string;
  snapshot: DispatchSnapshot;
  rendered: { email: RenderedEmail } | { api: RenderedApiRequest };
  sendsNothing: boolean;
}

export interface DispatchEnqueueBody {
  channel: DispatchChannel;
  operationKey: string;
  reviewedSnapshotSha256: string;
}

export interface DispatchEnqueueResult {
  dispatchId: string;
  status: DispatchStatus;
  documentVersion: number;
  snapshotSha256: string;
  replayed: boolean;
  purchaseOrderId: string;
  queuedOnly: boolean;
}

export interface DispatchEvent {
  id: string;
  event: string;
  actorUserId: string | null;
  detail: Record<string, unknown>;
  createdAt: string;
}

/** `GET .../history`: content and audit events, needs `purchase_orders.dispatch`. */
export interface DispatchHistoryPage {
  dispatch: {
    id: string;
    recipientEmail: string | null;
    snapshotSha256: string;
    snapshot: DispatchSnapshot;
    providerMessageId: string | null;
    supplierOrderRef: string | null;
    lastError: string | null;
  };
  events: DispatchEvent[];
  pagination: { hasMore: boolean; nextCursor: string | null };
}

export interface DispatchCancelResult {
  cancelled: { dispatchId: string; status: DispatchStatus }[];
  reconciliationRequired: { dispatchId: string; status: DispatchStatus }[];
  requestMayHaveLeft: boolean;
  supplierCancellation: 'not_performed';
  dispatch: DispatchSummary | null;
}

export interface DispatchResolveResult {
  dispatchId: string;
  status: 'sent' | 'failed';
  dispatch: DispatchSummary | null;
  nextStep: {
    purchaseOrderId: string;
    automaticResend: false;
    requiresNewPreview: true;
    requiresNewOperationKey: true;
  } | null;
}

export interface ChannelCapability {
  available: boolean;
  reason?: string;
}

export interface SupplierDispatchSettings {
  supplierId: string;
  mode: SupplierDispatchMode;
  orderEmail: string | null;
  orderDocumentLanguage: DocumentLanguage | null;
  /** What a dispatch would use: unset means the platform default. `null` when the stored value is unusable. */
  effectiveDocumentLanguage: DocumentLanguage | null;
  capability: { email: ChannelCapability; api: ChannelCapability };
  updatedAt: string;
}

/** `mode` always; the others: absent = unchanged, `null` = cleared. */
export interface SupplierDispatchSettingsBody {
  mode: SupplierDispatchMode;
  orderEmail?: string | null;
  orderDocumentLanguage?: DocumentLanguage | null;
}

export interface ResolveDispatchBody {
  decision: 'sent' | 'not_sent';
  reason: string;
  supplierOrderRef?: string;
}
